import React, { createContext, useContext, useMemo, ReactNode, useCallback, useState, useEffect } from 'react';
import enTranslations from '@/locales/en.json';
import ptTranslations from '@/locales/pt.json';
import { supabase } from '@/integrations/supabase/client';

export interface I18nContextType {
  t: (key: string, params?: Record<string, string | number>) => string;
  currentLanguage: 'pt' | 'en';
  changeLanguage: (language: 'pt' | 'en') => Promise<void>;
  isLoading: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<'pt' | 'en'>(() => {
    // Initialize with localStorage, fallback to browser language, then to English
    if (typeof window !== 'undefined') {
      const savedLanguage = localStorage.getItem('preferred_language') as 'pt' | 'en';
      if (savedLanguage && (savedLanguage === 'pt' || savedLanguage === 'en')) {
        return savedLanguage;
      }
      return navigator.language.startsWith('pt') ? 'pt' : 'en';
    }
    return 'en';
  });

  // Translations are imported statically - Vite handles cache busting automatically
  // In production, each build generates new file hashes, so old cached versions won't be used
  // The service worker (vite-plugin-pwa) detects changes and forces a reload
  const translations = useMemo(() => {
    return currentLanguage === 'pt' ? ptTranslations : enTranslations;
  }, [currentLanguage]);

  // Load user's preferred language from profile when user changes
  // If no preference is set, detect from country (Brazil -> PT, Other -> EN)
  useEffect(() => {
    const loadUserLanguage = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('preferred_language, country, timezone')
            .eq('id', user.id)
            .single();
          
          // If user has a saved preference, use it
          if (profile?.preferred_language && 
              (profile.preferred_language === 'pt' || profile.preferred_language === 'en')) {
            setCurrentLanguage(profile.preferred_language);
            localStorage.setItem('preferred_language', profile.preferred_language);
            return;
          }

          // No preference set - detect from country/timezone
          // Brazil -> Portuguese, Other countries -> English
          const { getUserCountry } = await import('@/utils/countryDetection');
          const country = await getUserCountry(user.id);
          const detectedLanguage = country === 'BR' ? 'pt' : 'en';
          
          setCurrentLanguage(detectedLanguage);
          localStorage.setItem('preferred_language', detectedLanguage);
          
          // Save detected language to profile for future sessions
          await supabase
            .from('profiles')
            .update({ preferred_language: detectedLanguage })
            .eq('id', user.id);
        } else {
          // No user - detect from browser timezone
          const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const { isBrazilianTimezone } = await import('@/utils/countryDetection');
          const detectedLanguage = isBrazilianTimezone(browserTimezone) ? 'pt' : 'en';
          setCurrentLanguage(detectedLanguage);
          localStorage.setItem('preferred_language', detectedLanguage);
        }
      } catch (error) {
        console.error('Error loading user language preference:', error);
        // Fallback to browser language
        const browserLang = navigator.language.startsWith('pt') ? 'pt' : 'en';
        setCurrentLanguage(browserLang);
      }
    };

    loadUserLanguage();
  }, []);

  const changeLanguage = useCallback(async (language: 'pt' | 'en') => {
    setCurrentLanguage(language);
    
    // Save to localStorage
    localStorage.setItem('preferred_language', language);
    
    // Save to database if user is logged in
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('profiles')
          .update({ preferred_language: language })
          .eq('id', user.id);
      }
    } catch (error) {
      console.error('Error saving language preference to database:', error);
    }
  }, []);


  const t = useMemo(() => {
    const resolve = (obj: any, keys: string[]): string | undefined => {
      let value: any = obj;
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          return undefined;
        }
      }
      return typeof value === 'string' ? value : undefined;
    };

    return (key: string, params?: Record<string, string | number>): string => {
      const keys = key.split('.');
      let value = resolve(translations, keys);

      if (value === undefined && currentLanguage !== 'en') {
        value = resolve(enTranslations, keys);
      }

      if (value === undefined) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Translation key not found: ${key}`);
        }
        return key;
      }

      // Replace parameters if provided — supports both {{key}} and {key} syntax
      if (params) {
        return value
          .replace(/\{\{(\w+)\}\}/g, (match, paramKey) =>
            params[paramKey]?.toString() ?? match
          )
          .replace(/\{(\w+)\}/g, (match, paramKey) =>
            params[paramKey]?.toString() ?? match
          );
      }

      return value;
    };
  }, [translations, currentLanguage]);

  const contextValue = useMemo(() => ({
    t,
    currentLanguage,
    changeLanguage,
    isLoading: false
  }), [t, currentLanguage, changeLanguage]);

  return (
    <I18nContext.Provider value={contextValue}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    // Provide fallback context instead of throwing error
    console.warn('useI18n called outside provider, using fallback context');
    
    const fallbackContext: I18nContextType = {
      t: (key: string) => key, // Return key as fallback
      currentLanguage: 'en',
      changeLanguage: async () => {},
      isLoading: false
    };
    
    return fallbackContext;
  }
  return context;
};
